const KEY = "pr.theme";          // "light" | "dark" | "auto"
const GEO_KEY = "pr.theme.geo";  // {lat, lon}
const META = { light: "#FAF7F0", dark: "#131120" };
const RAD = Math.PI / 180;
const DAY_MS = 86400000;

// Approximate sunrise/sunset for a mid-latitude when we have no coordinates.
const FALLBACK_RISE = [7.3, 6.9, 6.3, 6.5, 5.9, 5.6, 5.8, 6.3, 6.8, 7.3, 6.8, 7.2];
const FALLBACK_SET = [17.0, 17.6, 18.2, 19.8, 20.3, 20.6, 20.5, 20.0, 19.2, 18.4, 16.9, 16.7];

const atHour = (date, hours) => {
  const d = new Date(date);
  d.setHours(Math.floor(hours), Math.round((hours % 1) * 60), 0, 0);
  return d;
};

export const storedGeo = () => {
  try {
    const raw = JSON.parse(localStorage.getItem(GEO_KEY) || "null");
    return raw && typeof raw.lat === "number" && typeof raw.lon === "number" ? raw : null;
  } catch {
    return null;
  }
};

/** Standard sunrise equation, accurate to about a minute, no network needed. */
export const sunTimes = (date = new Date(), geo = storedGeo()) => {
  if (!geo) {
    const m = date.getMonth();
    return { sunrise: atHour(date, FALLBACK_RISE[m]), sunset: atHour(date, FALLBACK_SET[m]), approx: true };
  }
  const { lat, lon } = geo;
  const n =
    Math.floor((Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) - Date.UTC(2000, 0, 1)) / DAY_MS) +
    0.5 -
    lon / 360;
  const M = (357.5291 + 0.98560028 * n) % 360;
  const C = 1.9148 * Math.sin(M * RAD) + 0.02 * Math.sin(2 * M * RAD) + 0.0003 * Math.sin(3 * M * RAD);
  const L = (M + C + 180 + 102.9372) % 360;
  const jTransit = 2451545.0 + n + 0.0053 * Math.sin(M * RAD) - 0.0069 * Math.sin(2 * L * RAD);
  const decl = Math.asin(Math.sin(L * RAD) * Math.sin(23.44 * RAD));
  const cosOmega = (Math.sin(-0.833 * RAD) - Math.sin(lat * RAD) * Math.sin(decl)) / (Math.cos(lat * RAD) * Math.cos(decl));
  if (cosOmega > 1 || cosOmega < -1) return null; // polar day / night
  const omega = Math.acos(cosOmega) / RAD;
  const toDate = (j) => new Date((j - 2440587.5) * DAY_MS);
  return { sunrise: toDate(jTransit - omega / 360), sunset: toDate(jTransit + omega / 360), approx: false };
};

export const isNightNow = (now = new Date()) => {
  const t = sunTimes(now);
  if (!t) return now.getHours() >= 19 || now.getHours() < 7;
  return now >= t.sunset || now < t.sunrise;
};

export const storedMode = () => {
  try {
    const v = localStorage.getItem(KEY);
    return v === "dark" || v === "light" || v === "auto" ? v : null;
  } catch {
    return null;
  }
};

export const resolveTheme = (mode) => (mode === "auto" ? (isNightNow() ? "dark" : "light") : mode);

export const applyTheme = (theme) => {
  document.documentElement.setAttribute("data-theme", theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", META[theme]);
  window.dispatchEvent(new CustomEvent("pr-theme-change", { detail: theme }));
};

let watcher = null;
const startWatcher = () => {
  if (watcher) return;
  const tick = () => {
    if (storedMode() !== "auto" && storedMode() !== null) return;
    const next = resolveTheme("auto");
    if (document.documentElement.getAttribute("data-theme") !== next) applyTheme(next);
  };
  watcher = setInterval(tick, 5 * 60 * 1000);
  document.addEventListener("visibilitychange", () => { if (!document.hidden) tick(); });
};

/** Dark mode is retired, the app is always light. Clears any stored night-mode choice. */
export const initTheme = () => {
  try { localStorage.removeItem(KEY); } catch { /* storage unavailable */ }
  applyTheme("light");
  return "light";
};

export const setMode = (mode) => {
  try { localStorage.setItem(KEY, mode); } catch { /* storage unavailable */ }
  applyTheme(resolveTheme(mode));
  startWatcher();
  return mode;
};

/** One-time permission prompt so "auto" uses real local sunset instead of the estimate. */
export const requestLocation = () =>
  new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error("This browser can't share a location.")); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const geo = { lat: Math.round(pos.coords.latitude * 100) / 100, lon: Math.round(pos.coords.longitude * 100) / 100 };
        try { localStorage.setItem(GEO_KEY, JSON.stringify(geo)); } catch { /* noop */ }
        if ((storedMode() || "auto") === "auto") applyTheme(resolveTheme("auto"));
        resolve(geo);
      },
      () => reject(new Error("Location permission was declined.")),
      { timeout: 8000, maximumAge: 6 * 3600 * 1000 },
    );
  });

export const formatTime = (date) =>
  date ? date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
